import { showVisualSearchTipsDialog } from './dialogs/VisualSearchTipsDialog.js';

import { registerEventHandlers as registerFontSizeHandlers } from './mixins/settings-bar/FontSizeChange.js';
import { registerEventHandlers as registerSampleTextHandlers } from './mixins/settings-bar/SampleTextChange.js';
import { registerEventHandlers as registerViewOptionHandlers } from './mixins/settings-bar/ViewOptionChange.js';

import { registerEventHandlers as registerVariationCardAddToWebProjects } from './mixins/variation-card/VariationCardAddToWebProject.js';
import { registerEventHandlers as registerVariationCardFavoriteHandlers } from './mixins/variation-card/VariationCardFavorite.js';
import { registerEventHandlers as registerVariationCardSyncHandlers } from './mixins/variation-card/VariationCardSync.js';
import { registerEventHandlers as registerVariationCardViewHandlers } from './mixins/variation-card/VariationCardView.js';
import { registerEventHandlers as registerSaveToLibraryHandlers } from './mixins/SaveToLibrary.js';

import { getCropper } from '../util/cropper.js';
import { getData } from '../util/script_data.js';
import { addPageAction } from '../util/new_relic.js';
import UrlService from '../util/url_service.js';
import { putVisualSearch } from '../util/visual_search_service.js';
import { getBlob } from '../util/secure_fetch.js';
import FontSizeSlider from '@adobe-fonts/site/components/settings-bar/font-size-slider/FontSizeSlider.js';

const STEPS = ['upload', 'select', 'results'] as const;
const [UPLOAD, SELECT, RESULTS] = STEPS;
type Step = typeof STEPS[number];

const URL_BASE_REGEX = /(fonts\/vs\/[^/]*)\/?(.*)/ ;
const MINIMUM_SKELETON_COUNT = 4;
const MOBILE_WINDOW_WIDTH = 768;

const ACTION_STEP_PREVIOUS = 'previous';
const ACTION_STEP_NEXT = 'next';

interface VisualSearchFont {
  name: string;
}

interface PreloadedData {
  step: Step;
  fonts: VisualSearchFont[];
  detectedText?: string;
  visualSearchOpaqueId?: string;
  coordinates?: Record<string, number>;
  detectedRegion?: Record<string, number>;
}

/** Cropper.js instance from `getCropper` (no project typings for cropperjs). */
interface CropperInstance {
  getData(): { x: number; y: number; width: number; height: number };
  getCroppedCanvas(): HTMLCanvasElement;
}

class VisualSearchPage extends HTMLElement {
  private stickyObserver?: IntersectionObserver;
  private cropper?: CropperInstance;
  private image?: HTMLImageElement;
  private confirmationCanvas: Element | null = null;
  private stepAbortController?: AbortController;

  fonts: VisualSearchFont[] = [];
  extractedText = '';
  visualSearchOpaqueId = '';

  connectedCallback() {
    this.updateStepUI(this.currentStep);
    this.setStep(this.currentStep);
    this.loadData();

    window.addEventListener('popstate', this.initPopstateHandler);

    registerFontSizeHandlers(this);
    registerSampleTextHandlers(this);
    registerViewOptionHandlers(this);

    registerVariationCardAddToWebProjects(this);
    registerVariationCardFavoriteHandlers(this);
    registerVariationCardSyncHandlers(this);
    registerVariationCardViewHandlers(this);
    registerSaveToLibraryHandlers(this);

    this.addEventListener('click', this.toggleVisualSearchTipsDialog);
    this.addEventListener('click', this.initStepClickHandlers);
  }

  disconnectedCallback() {
    window.removeEventListener('popstate', this.initPopstateHandler);
    this._unstickButton?.removeEventListener('click', this.dismissConfirmImage);
    this.removeEventListener('click', this.initStepClickHandlers);
    this.removeEventListener('click', this.toggleVisualSearchTipsDialog);

    this.stickyObserver?.disconnect();
    this.stepAbortController?.abort('disconnect');
  }

  get currentStep(): Step {
    return (this.dataset.step as Step) || UPLOAD;
  }

  get emptyStateElement() {
    return this.querySelector('#visual-search-empty-state') as HTMLElement;
  }

  get fontCountElement() {
    return this.querySelector('.visual-searches__similar-fonts-count') as HTMLElement;
  }

  get resultsElement() {
    return this.querySelector('#visual-search-results') as HTMLElement;
  }

  get _variationCardsContainer() {
    return this.querySelector('#visual-search-variation-cards') as HTMLElement;
  }

  toggleVisualSearchTipsDialog = (event: Event) => {
    if (!(event.target instanceof Element)) {
      return;
    }
    const button = event.target.closest('[data-id="visual-search-tips-button"]');
    if (!button) {
      return;
    }

    showVisualSearchTipsDialog();
  };

  setStep(step: Step) {
    if (!STEPS.includes(step) || step === this.currentStep) {
      return;
    }

    // Abort any in-flight async work from a previous step transition
    this.stepAbortController?.abort('cancel');
    this.stepAbortController = new AbortController();
    const { signal } = this.stepAbortController;

    if (this.currentStep !== UPLOAD) {
      this.storeDataInUrl();
    }

    // When transitioning from select → results, show the cropped image and skeleton
    // cards immediately, then fetch real results in the background.
    if (this.currentStep === SELECT && step === RESULTS && this.cropper) {
      this.showResultsLoading();
      this.showCroppedImage();
      this.applyStep(step);
      this.updateResultsForCoordinates(signal).then(() => {
        if (signal.aborted) {
          return;
        }
        this.hideFontCount();
      }).catch((error) => {
        if (!signal.aborted) {
          throw error;
        }
      });
      return;
    }

    this.applyStep(step);
  }

  private applyStep(step: Step) {
    this.setAttribute('data-step', step);

    this.updateStepUI(step);

    addPageAction('typekit.render.visual-search.step', { step });

    const match = URL_BASE_REGEX.exec(window.location.pathname);
    if (match) {
      const urlBase = match[1];
      UrlService.pushState({}, '', '/' + urlBase + '/' + step);
    }

    if (step === RESULTS) {
      this.initConfirmationImageSticky();
    }
  }

  updateFontListState(isEmpty: boolean) {
    const resultsHeading = this.querySelector('#visual-search-results-heading') as HTMLElement;

    this.emptyStateElement?.classList.toggle('vs-visible', isEmpty);
    resultsHeading?.classList.toggle('vs-visible', !isEmpty);
    this.resultsElement?.classList.toggle('vs-visible', !isEmpty);
  }

  // Show the results area with skeleton cards while the API call is in flight
  private showResultsLoading() {
    this.resultsElement?.classList.add('vs-visible');

    if (!this._variationCardsContainer) {
      return;
    }

    const template = this.querySelector('#vs-skeleton-card-template');
    const skeletonCount = Math.max(this.fonts.length, MINIMUM_SKELETON_COUNT);
    const skeletons: HTMLElement[] = [];
    for (let i = 0; i < skeletonCount; i++) {
      const skeleton = (template as HTMLTemplateElement).content.cloneNode(true);
      skeletons.push(skeleton as HTMLElement);
    }
    this._variationCardsContainer.replaceChildren(...skeletons);

    this.hideFontCount();
    this.emptyStateElement?.classList.remove('vs-visible');
  }

  private hideFontCount() {
    this.fontCountElement?.classList.remove('vs-visible');
  }

  private preloadedData(): PreloadedData | undefined {
    return getData('/neue/preloaded_variation_data') as PreloadedData | undefined;
  }

  private async loadData() {
    const data = this.preloadedData();
    if (!data) {
      return;
    }

    this.confirmationCanvas = this.querySelector(
      '.visual-searches__confirm-image .visual-searches__confirmation-canvas-placeholder'
    );

    this.visualSearchOpaqueId = data.visualSearchOpaqueId || '';

    if (data.step !== UPLOAD) {
      this.fonts = data.fonts || [];
      this.extractedText = data.detectedText || '';
    }

    // On the results step, the cropper container (data-show-step="select") is hidden,
    // but Cropper.js needs it visible to measure dimensions after a page refresh, so temporarily show it.
    if (this.visualSearchOpaqueId) {
      const cropperContainer = document.getElementById('visual-searches__image-cropper');
      const needsTemporaryVisibility = this.currentStep === RESULTS;
      if (needsTemporaryVisibility) {
        cropperContainer?.classList.add('vs-visible');
      }
      this.setAttribute('data-image-loading', '');

      try {
        await this.initCropperImage(data);
      } catch (error) {
        console.error(error);
      } finally {
        if (needsTemporaryVisibility) {
          cropperContainer?.classList.remove('vs-visible');
        }
        this.removeAttribute('data-image-loading');
      }
    }

    // On a direct page load at the results step, setStep() returns early because
    // the step already matches, so applyStep() → initConfirmationImageSticky() is
    // never called. Set it up here once the image is ready.
    if (this.currentStep === RESULTS && !this.stickyObserver) {
      this.initConfirmationImageSticky();
    }
  }

  private async initCropperImage(data: PreloadedData): Promise<void> {
    await this.preloadImage();
    await this.initCropper(this.getCropperData(data));
    this.showCroppedImage();
  }

  private updateStepUI(step: Step) {
    const stepIndex = STEPS.indexOf(step);
    this.querySelectorAll<HTMLElement>('[data-step-item]').forEach(item => {
      const itemStep = item.getAttribute('data-step-item') as Step;
      const itemIndex = STEPS.indexOf(itemStep);
      item.classList.toggle('is-selected', itemStep === step);
      item.classList.toggle('is-complete', itemIndex < stepIndex);
    });

    this.querySelectorAll<HTMLElement>('[data-show-step]').forEach(el => {
      el.classList.toggle('vs-visible', el.getAttribute('data-show-step') === step);
    });
  }

  private initStepClickHandlers(event: Event) {
    const target = (event.target as HTMLElement).closest<HTMLElement>('[data-step-click]');
    if (!target || this.hasAttribute('data-image-loading')) {
      return;
    }

    const action = target.getAttribute('data-step-click');
    if (action === ACTION_STEP_NEXT) {
      const nextIndex = STEPS.indexOf(this.currentStep) + 1;
      if (nextIndex < STEPS.length) {
        this.setStep(STEPS[nextIndex]);
      }
    } else if (action === ACTION_STEP_PREVIOUS) {
      const prevIndex = STEPS.indexOf(this.currentStep) - 1;
      if (prevIndex >= 0) {
        this.setStep(STEPS[prevIndex]);
      }
    } else if (STEPS.includes(action as Step)) {
      this.setStep(action as Step);
    }
  }

  private get _confirmImage() {
    return this.querySelector('.visual-searches__confirm-image') as HTMLElement | null;
  }

  private get _unstickButton() {
    return this.querySelector('.sticky-nav__unstick-button') as HTMLElement | null;
  }

  private initConfirmationImageSticky() {
    const scrollContainer = document.getElementById('adobe-fonts-content');
    const sentinel = this.querySelector('.visual-searches__sticky-sentinel');

    if (!scrollContainer || !sentinel) {
      return;
    }

    // When scrolling down, show the close button for the confirm image
    // and when scrolled all the way up, hide the close button.
    this.stickyObserver = new IntersectionObserver(
      ([entry]) => {
        const confirmImage = this._confirmImage;
        const hasCroppedImage = confirmImage != null
          && !confirmImage.querySelector('.visual-searches__confirmation-canvas-placeholder');
        const isConfirmImageVisible = hasCroppedImage
          && confirmImage.offsetParent !== null
          && !confirmImage.classList.contains('visual-searches__confirm-image--dismissed');
        this._unstickButton?.classList.toggle(
          'sticky-nav__unstick-button--visible',
          !entry.isIntersecting && isConfirmImageVisible
        );
      },
      { root: scrollContainer, threshold: [1] }
    );
    this.stickyObserver.observe(sentinel);

    this._unstickButton?.addEventListener('click', this.dismissConfirmImage);
  }

  private dismissConfirmImage = () => {
    this._confirmImage?.classList.add('visual-searches__confirm-image--dismissed');
  };

  private initPopstateHandler = () => {
    this.setStep(this.getStepFromUrl());
  };

  private getStepFromUrl(): Step {
    const matches = URL_BASE_REGEX.exec(window.location.pathname);
    if (matches && matches[matches.length - 1]) {
      return matches[matches.length - 1] as Step;
    }
    return STEPS[0];
  }

  private async preloadImage(): Promise<void> {
    this.image = document.getElementById('visual-searches__image-cropper-image') as HTMLImageElement;
    if (!this.image) {
      return;
    }

    const src = this.image.getAttribute('data-src') || this.image.src;
    if (!src) {
      return;
    }

    const response = await getBlob(src);
    const blob = await response.blob();

    return new Promise<void>((resolve) => {
      this.image!.onload = () => resolve();
      this.image!.src = URL.createObjectURL(blob);
    });
  }

  private async initCropper(data: Record<string, number>): Promise<void> {
    if (!this.image) {
      return;
    }

    const container = document.getElementById('visual-searches__image-cropper');
    if (container) {
      container.style.maxWidth = this.image.naturalWidth + 'px';
      container.style.maxHeight = this.image.naturalHeight + 'px';
    }

    this.cropper = await getCropper(this.image, {
      background: false,
      data,
      minCropBoxHeight: 10,
      minCropBoxWidth: 10,
      minContainerHeight: 10,
      minContainerWidth: 10,
      dragMode: 'none',
      responsive: true,
      zoomable: false,
      checkCrossOrigin: false,
      viewMode: 1
    });
  }

  private getCropperData(data: PreloadedData): Record<string, number> {
    if (data.coordinates && Object.keys(data.coordinates).length != 0) {
      return {
        x: data.coordinates.x_min,
        y: data.coordinates.y_min,
        width: data.coordinates.x_max - data.coordinates.x_min,
        height: data.coordinates.y_max - data.coordinates.y_min
      };
    }

    // Otherwise, pick a region from the list of detected regions (if there are any).
    if (data.detectedRegion && Object.keys(data.detectedRegion).length != 0) {
      const region = data.detectedRegion;
      return {
        x: region.x_min,
        y: region.y_min,
        width: region.x_max - region.x_min,
        height: region.y_max - region.y_min
      };
    }

    // Fallback on an empty object if nothing was detected.
    return {};
  }

  private storeDataInUrl() {
    const params = this.getCropperCoordinates();

    UrlService.setSearchParams(params);
  }

  private getCropperCoordinates(): Record<string, number> {
    const data = this.cropper?.getData();
    if (!data) {
      return {};
    }

    // cropper.js returns sub-pixel floats (e.g. 152.99999999999997) and, at the image
    // edge, tiny negatives (e.g. -1.5e-14). Round to whole pixels and clamp to >= 0 so
    // we don't bloat logs/URLs or trip the recognition API's ge=0 check.
    const clamp = (value: number) => Math.max(0, Math.round(value));
    const cropperCoordinates = {
      x_min: clamp(data.x),
      y_min: clamp(data.y),
      x_max: clamp(data.x + data.width),
      y_max: clamp(data.y + data.height)
    };
    return cropperCoordinates;
  }

  private showCroppedImage() {
    if (!this.cropper || !this.confirmationCanvas?.parentNode) {
      return;
    }
    const croppedCanvas = this.cropper.getCroppedCanvas();
    this.confirmationCanvas.parentNode.replaceChild(croppedCanvas, this.confirmationCanvas);
    this.confirmationCanvas = croppedCanvas;
  }

  private async updateResultsForCoordinates(signal?: AbortSignal) {
    const response = await putVisualSearch(this.visualSearchOpaqueId, this.getCropperCoordinates(), signal);
    if (signal?.aborted) {
      return;
    }

    this.extractedText = response.text;
    this.fonts = response.fonts;

    const hasCards = !!response.content && !!response.content.variationCards;
    if (this.fonts.length === 0 || !hasCards) {
      return this.updateFontListState(true);
    }

    // Update the sample text menu with the newly detected text from the crop region
    const sampleTextMenu = this.querySelector('af-sample-text-menu') as HTMLInputElement | null;
    if (sampleTextMenu && this.extractedText) {
      sampleTextMenu.setAttribute('value', this.extractedText);
    }

    // Replace variation cards with server-rendered HTML, applying the detected text
    // and current font size to each af-display-in-font element inside the cards.
    if (this._variationCardsContainer && hasCards) {
      const parsed = new DOMParser().parseFromString(response.content.variationCards, 'text/html');
      const fontSizeSlider = this.querySelector('af-font-size-slider') as HTMLElement & FontSizeSlider | null;

      const sampleText = this.extractedText || sampleTextMenu?.value;
      const sampleTextSize = fontSizeSlider?.fontSizeValue;
      const isMobile = window.innerWidth <= MOBILE_WINDOW_WIDTH;

      parsed.querySelectorAll<HTMLElement>('af-variation-card').forEach((card) => {
        card.toggleAttribute('isMobile', isMobile);

        const samples = card.querySelectorAll('[data-editable-sample]') as NodeListOf<HTMLElement>;
        samples.forEach((s: HTMLElement) => {
          if (sampleText) {
            s.setAttribute('previewstring', sampleText);
            s.textContent = sampleText;
          }
          if (sampleTextSize) {
            s.style.setProperty('font-size', `${sampleTextSize}px`);
          }
        });
      });

      this._variationCardsContainer.replaceChildren(...parsed.body.children);
    }

    if (this.fontCountElement && response.content != null && response.content.similarFontsCount != null) {
      this.fontCountElement.textContent = response.content.similarFontsCount;
    }

    const actionData: { fontCount: number; topResult?: string } = { fontCount: this.fonts.length };
    if (this.fonts.length > 0) {
      actionData.topResult = this.fonts[0]?.name;
    }
    addPageAction('typekit.render.visual-search', actionData);
  }
}

customElements.define('af-visual-search-page', VisualSearchPage);

export { UPLOAD, SELECT, RESULTS };
export default VisualSearchPage;
